// src/components/ClientSwitcher.jsx
// The client switcher, left of the menu bar, centred in the space between
// the left side menu and the menu (3.49.13, computers;
// the owner's Claude Design handoff "Client Switcher 1a"). Collapsed, it's a
// round avatar with the client's initials and a small swap badge that flips
// now and then; pointed at (or focused), it widens from its middle, to both
// sides, into "Switch client / <name>" (centred, as in the design). A click:
//   - 2 choices: swaps to the other one straight away
//   - 3 or more: opens a short list (scrolls after about 5), ✓ on the one
//     showing
// Super Admins get "All clients" and each client; people in two or more
// clients get theirs; hidden for everyone else and in read-only View as.
// Phones (owner, Oct 9) use it too, in the header: a tap widens it and opens
// the list in one step, and the list opens from its left edge (align
// 'start') so it stays on the screen. It replaced the old AccountSwitcher.
// The trigger keeps the design's ink colour in every theme; the list uses
// the theme's colours (light and dark).
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const PALETTE = ['#5B4BE0', '#E0673B', '#1F9D6B', '#C2378A', '#2F7FD1', '#B8860B', '#8A3FD1', '#3A3940', '#1C8C9E', '#D14B4B'];
const INK = '#17161A';
const EASE = 'cubic-bezier(.3,1.3,.5,1)';
const CLOSE_MS = 180;

// "All clients" → AC, "Clinicmind" → CL
export const initials = name => {
  const w = String(name || '').trim().split(/\s+/).filter(Boolean);
  return (w.length >= 2 ? w[0][0] + w[1][0] : (w[0] || '?').slice(0, 2)).toUpperCase();
};

function SwapIcon({ size = 16, color = INK }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 4 3 8l4 4" /><path d="M3 8h14" /><path d="m17 20 4-4-4-4" /><path d="M21 16H7" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5 10 17l9-10" />
    </svg>
  );
}

export default function ClientSwitcher({ align = 'center' }) {
  const atStart = align === 'start';
  const { effectiveInfo, switchAccount, viewAs } = useAuth();
  const info = effectiveInfo || {};
  const superView = info.level === 'super_admin';
  const word = superView ? 'client' : 'company';   // 3.67.5 (owner): the people at a client switch company
  const accounts = info.accounts || [];
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hi, setHi] = useState(0);
  const [fade, setFade] = useState({ top: false, bottom: false });
  const wrapRef = useRef(null), btnRef = useRef(null), listRef = useRef(null), timer = useRef(null);

  const options = [...(superView ? [{ id: 'all', name: 'All clients' }] : []), ...accounts]
    .map((o, i) => ({ ...o, color: o.color || PALETTE[i % PALETTE.length] }));
  const value = info.accountId || (superView ? 'all' : accounts[0]?.id || '');
  const current = options.find(o => o.id === value) || options[0];
  const expanded = hovered || open;

  // Outside click and Escape close the list
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => { if (!wrapRef.current?.contains(e.target)) { setOpen(false); setHovered(false); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  useEffect(() => () => clearTimeout(timer.current), []);
  // The list's top and bottom fades follow its scroll
  const updateFade = () => {
    const el = listRef.current;
    if (!el) return;
    const can = el.scrollHeight > el.clientHeight;
    setFade({ top: can && el.scrollTop > 2, bottom: can && el.scrollTop + el.clientHeight < el.scrollHeight - 2 });
  };
  useEffect(() => { if (open) { requestAnimationFrame(updateFade); listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' }); } }, [open, options.length]);

  // Arrow keys move the focus along the list
  useEffect(() => { if (open) listRef.current?.querySelectorAll('[role="option"]')[hi]?.focus(); }, [hi, open]);

  if (!superView && accounts.length < 2) return null;
  if (viewAs?.readOnly || !current) return null;

  const enter = () => { clearTimeout(timer.current); setHovered(true); };
  const leave = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { setHovered(false); setOpen(false); }, CLOSE_MS); };

  async function pick(id) {
    setOpen(false); setHovered(false);
    if (id === value) return;
    setBusy(true);
    try { await switchAccount(id); } finally { setBusy(false); btnRef.current?.focus(); }
  }
  function click() {
    if (busy || options.length < 2) return;
    if (options.length === 2) { pick(options.find(o => o.id !== value).id); return; }
    setOpen(o => !o);
    setHi(Math.max(0, options.findIndex(o => o.id === value)));
  }
  function onKey(e) {
    if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); btnRef.current?.focus(); return; }
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(options.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
    else if (e.key === 'Enter' && options[hi] && document.activeElement !== btnRef.current) { e.preventDefault(); pick(options[hi].id); }
  }

  return (
    <div ref={wrapRef} style={S.wrap} onMouseEnter={enter} onMouseLeave={leave} onKeyDown={onKey}>
      <style>{CSS}</style>
      <button ref={btnRef} type="button" data-tour="client-switcher" onClick={click} onFocus={enter} onBlur={e => { if (!wrapRef.current?.contains(e.relatedTarget)) leave(); }}
        aria-haspopup={options.length > 2 ? 'listbox' : undefined} aria-expanded={options.length > 2 ? open : undefined}
        aria-label={`Switch ${word}, current: ${current.name}`} title={options.length === 2 ? `Switch to ${options.find(o => o.id !== value).name}` : undefined}
        className={`cs-trigger${expanded ? ' cs-expanded' : ''}`}
        style={{ ...S.trigger, ...(expanded ? S.triggerOpen : {}), cursor: options.length < 2 ? 'default' : 'pointer' }}>
        <span style={{ ...S.avatar, background: current.color }}>{initials(current.name)}</span>
        <span className="cs-label" style={{ ...S.label, maxWidth: expanded ? 200 : 0, opacity: expanded ? 1 : 0, transform: expanded ? 'translateX(0)' : 'translateX(-6px)', transitionDelay: expanded ? '.08s, 0s' : '0s' }} aria-hidden={!expanded}>
          <span style={S.labelSmall}>Switch {word}</span>
          <span style={S.labelName}>{current.name}</span>
        </span>
        <span className={`cs-badge${expanded ? '' : ' cs-idle'}${busy ? ' cs-busy' : ''}`}
          style={expanded ? { ...S.badge, ...S.badgeOpen, transform: open ? 'rotate(90deg)' : 'none' } : S.badge}>
          <SwapIcon color={expanded ? '#FFFFFF' : INK} />
        </span>
      </button>
      {open && (
        <>
          <div style={atStart ? { ...S.bridge, left: 0, marginLeft: 0 } : S.bridge} aria-hidden="true" />
          <div className={atStart ? 'cs-menu cs-menu-start' : 'cs-menu'} style={atStart ? { ...S.menu, left: 0, transformOrigin: 'top left' } : S.menu}>
            <div style={S.menuHead}>Switch {word} · {options.length}</div>
            <div style={{ position: 'relative' }}>
              <div ref={listRef} onScroll={updateFade} className="cs-list" role="listbox" aria-label="Clients" style={S.list}>
                {options.map((o, i) => {
                  const on = o.id === value;
                  return (
                    <button key={o.id} type="button" role="option" aria-selected={on} tabIndex={i === hi ? 0 : -1}
                      onMouseEnter={() => setHi(i)} onClick={() => pick(o.id)} className="cs-row"
                      style={{ ...S.row, ...(on ? S.rowOn : {}) }}>
                      <span style={{ ...S.rowAvatar, background: o.color }}>{initials(o.name)}</span>
                      <span style={S.rowName}>{o.name}</span>
                      {superView && o.type && <span style={S.type}>{o.type === 'individual' ? 'Individual' : 'Business'}</span>}
                      {on && <span style={S.check}><CheckIcon /></span>}
                    </button>
                  );
                })}
              </div>
              {fade.top && <div style={S.fadeTop} aria-hidden="true" />}
              {fade.bottom && <div style={S.fadeBottom} aria-hidden="true" />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const CSS = `
@keyframes cs-nudge { 0%,70%,100% { transform: scale(.7) rotate(0) } 80%,90% { transform: scale(.7) rotate(-180deg) } }
@keyframes cs-spin { to { transform: rotate(360deg) } }
@keyframes cs-menu-in { from { opacity: 0; transform: translate(-50%, -6px) scale(.97) } to { opacity: 1; transform: translate(-50%, 0) scale(1) } }
.cs-badge.cs-idle { animation: cs-nudge 4s ease-in-out infinite; }
.cs-badge.cs-busy svg { animation: cs-spin .7s linear infinite; }
.cs-menu { animation: cs-menu-in .22s ease-out forwards; }
@keyframes cs-menu-in-start { from { opacity: 0; transform: translateY(-6px) scale(.97) } to { opacity: 1; transform: none } }
.cs-menu.cs-menu-start { animation-name: cs-menu-in-start; }
.cs-row:hover, .cs-row:focus-visible { background: var(--c-f0faf8) !important; outline: none; }
.cs-list::-webkit-scrollbar { width: 6px } .cs-list::-webkit-scrollbar-thumb { background: var(--c-cfd8dc); border-radius: 6px } .cs-list::-webkit-scrollbar-track { background: transparent }
@media (prefers-reduced-motion: reduce) {
  .cs-badge.cs-idle { animation: none; transform: scale(.7) }
  .cs-trigger { transition: width .01s, box-shadow .2s !important }
  .cs-menu { animation: none; transform: translate(-50%, 0) }
  .cs-menu.cs-menu-start { transform: none }
}`;

const S = {
  // centred: the trigger grows from its middle to both sides (the design's wrapper)
  wrap: { position: 'relative', display: 'flex', justifyContent: 'center', pointerEvents: 'auto' },
  trigger: { position: 'relative', display: 'flex', alignItems: 'center', gap: 10, width: 48, height: 48, padding: '0 8px', boxSizing: 'border-box',
    borderRadius: 999, border: 'none', background: INK, boxShadow: '0 2px 6px rgba(23,22,26,.2)', overflow: 'visible', fontFamily: 'inherit',
    transition: `width .4s ${EASE}, box-shadow .2s` },
  triggerOpen: { width: 232, overflow: 'hidden', boxShadow: '0 10px 24px -8px rgba(23,22,26,.45)' },
  avatar: { width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: 12, fontWeight: 600, letterSpacing: '.02em' },
  label: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.15, minWidth: 0, flex: 1, overflow: 'hidden', whiteSpace: 'nowrap',
    transition: `opacity .25s, transform .3s ${EASE}`, textAlign: 'left' },
  labelSmall: { fontSize: 11, color: '#A9A6AE', whiteSpace: 'nowrap' },
  labelName: { fontSize: 14, fontWeight: 500, color: '#FFFFFF', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  badge: { position: 'absolute', right: -2, bottom: -2, width: 20, height: 20, borderRadius: '50%', background: '#FFFFFF', boxShadow: `0 0 0 2px ${INK}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'scale(.7)', transition: `all .3s ${EASE}` },
  badgeOpen: { position: 'static', width: 32, height: 32, flexShrink: 0, background: '#2C2B31', boxShadow: 'none', animation: 'none' },
  bridge: { position: 'absolute', top: 48, left: '50%', marginLeft: -124, width: 248, height: 14 },
  menu: { position: 'absolute', top: 58, left: '50%', width: 248, zIndex: 320, background: 'var(--c-ffffff)', borderRadius: 14, padding: 6, boxSizing: 'border-box',
    boxShadow: '0 0 0 1px var(--c-e0e0e0), 0 18px 40px -12px rgba(23,22,26,.35)', transformOrigin: 'top center' },
  menuHead: { fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '.06em',
    color: 'var(--t-607d8b)', padding: '8px 10px 6px' },
  list: { maxHeight: 236, overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column', gap: 2, scrollbarWidth: 'thin', scrollbarColor: 'var(--c-cfd8dc) transparent' },
  row: { display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '7px 10px', borderRadius: 10, border: 'none', background: 'transparent', width: '100%',
    fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: 'var(--t-263238)', cursor: 'pointer', textAlign: 'left', boxSizing: 'border-box', flexShrink: 0 },
  rowOn: { background: 'var(--c-f0faf8)', fontWeight: 600 },
  rowAvatar: { width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF', fontSize: 10, fontWeight: 600 },
  rowName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  type: { fontSize: 10.5, fontWeight: 600, color: 'var(--t-607d8b)', flexShrink: 0 },
  check: { display: 'flex', color: 'var(--t-263238)', flexShrink: 0 },
  fadeTop: { position: 'absolute', top: 0, left: 0, right: 8, height: 24, pointerEvents: 'none', background: 'linear-gradient(var(--c-ffffff), transparent)' },
  fadeBottom: { position: 'absolute', bottom: 0, left: 0, right: 8, height: 28, pointerEvents: 'none', background: 'linear-gradient(transparent, var(--c-ffffff))' },
};
